/**
 * ReviewChip — compact header-area review control for admin edit modals.
 *
 * Replaces the full ReviewSection block. Lives inline in the DialogHeader
 * beside the Question ID row so it occupies zero content-area space.
 *
 * States:
 *   Not flagged → ghost "Mark for Review" button → click opens popover
 *   Flagged     → amber pill chip (click = edit popover, hover = remarks tooltip)
 *                 + single-click ✓ resolve button beside it
 *
 * Props:
 *   question  — current question doc (needs id + review fields)
 *   kind      — 'oral' | 'written'
 *   categories — array from useReviewConfig()
 *   enabled   — bool
 *   onSaved(updatedQuestion) — callback after successful PATCH
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClipboardPen, Pencil, CheckCircle2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const REMARKS_MAX = 2000;

const ReviewChip = ({ question, kind, categories = [], enabled = false, onSaved }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [categoryId, setCategoryId]   = useState('__none__');
  const [remarks, setRemarks]         = useState('');
  const [saving, setSaving]           = useState(false);
  const [resolving, setResolving]     = useState(false);

  if (!enabled || !question?.id) return null;

  const isFlagged      = !!question?.needs_review;
  const activeCategory = categories.find((c) => c.id === question?.review_category_id);

  const openPopover = () => {
    setCategoryId(question?.review_category_id || '__none__');
    setRemarks(question?.review_remarks || '');
    setPopoverOpen(true);
  };

  const handleFlag = async () => {
    setSaving(true);
    try {
      const payload = {
        needs_review: true,
        review_category_id: categoryId === '__none__' ? null : categoryId,
        review_remarks: remarks.trim() || null,
      };
      const res = await axios.patch(
        `${API}/api/admin/review/${kind}/${question.id}/flag`,
        payload,
        { headers: authHeaders() },
      );
      toast.success(isFlagged ? 'Review flag updated' : 'Flagged for review');
      setPopoverOpen(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async () => {
    setResolving(true);
    try {
      const res = await axios.patch(
        `${API}/api/admin/review/${kind}/${question.id}/flag`,
        { needs_review: false },
        { headers: authHeaders() },
      );
      toast.success('Review resolved');
      setPopoverOpen(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  const popoverForm = (
    <PopoverContent
      align="start"
      side="bottom"
      className="w-80 p-0 shadow-lg"
      data-testid={`review-chip-popover-${kind}`}
    >
      {/* Header */}
      <div className="px-4 py-2.5 border-b flex items-center justify-between bg-gray-50 rounded-t-md">
        <span className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <ClipboardPen className="w-3.5 h-3.5 text-amber-600" />
          {isFlagged ? 'Edit Review Flag' : 'Flag for Review'}
        </span>
        <button
          onClick={() => setPopoverOpen(false)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          data-testid={`review-chip-popover-close-${kind}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Form */}
      <div className="px-4 py-3 space-y-3">
        <div>
          <label className="text-[11px] font-medium text-gray-500 block mb-1">
            Category <span className="text-gray-400">(optional)</span>
          </label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="h-8 text-xs" data-testid={`review-chip-category-${kind}`}>
              <SelectValue placeholder="Uncategorized" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Uncategorized</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="inline-flex items-center gap-1.5">
                    {c.color && (
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.color }} />
                    )}
                    {c.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-[11px] font-medium text-gray-500 block mb-1">
            Remarks <span className="text-gray-400">(optional)</span>
          </label>
          <Textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value.slice(0, REMARKS_MAX))}
            placeholder="Why does this need review?"
            rows={2}
            className="text-xs resize-none"
            data-testid={`review-chip-remarks-${kind}`}
          />
          <div className="text-[10px] text-gray-400 text-right mt-0.5">
            {remarks.length}/{REMARKS_MAX}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-3 flex items-center justify-between gap-2">
        {isFlagged ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleResolve}
            disabled={resolving || saving}
            className="h-7 text-xs text-emerald-700 hover:bg-emerald-50"
            data-testid={`review-chip-resolve-popup-${kind}`}
          >
            {resolving
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <CheckCircle2 className="w-3 h-3 mr-1" />
            }
            Resolve
          </Button>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPopoverOpen(false)}
            disabled={saving}
            className="h-7 text-xs"
            data-testid={`review-chip-cancel-${kind}`}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleFlag}
            disabled={saving}
            className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
            data-testid={`review-chip-save-${kind}`}
          >
            {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            {isFlagged ? 'Update' : 'Flag for Review'}
          </Button>
        </div>
      </div>
    </PopoverContent>
  );

  return (
    <TooltipProvider delayDuration={400}>
      <div className="inline-flex items-center gap-1" data-testid={`review-chip-root-${kind}`}>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          {!isFlagged ? (
            /* ── Not flagged: ghost trigger ── */
            <PopoverTrigger asChild>
              <button
                onClick={openPopover}
                className="inline-flex items-center gap-1 text-[11px] text-amber-600 hover:text-amber-800 hover:bg-amber-50 border border-dashed border-amber-300 px-1.5 py-0.5 rounded transition-colors"
                data-testid={`review-chip-flag-trigger-${kind}`}
              >
                <ClipboardPen className="w-3 h-3" />
                Mark for Review
              </button>
            </PopoverTrigger>
          ) : (
            /* ── Flagged: chip with tooltip for remarks ── */
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button
                    onClick={openPopover}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-2 py-0.5 rounded-full transition-colors"
                    data-testid={`review-chip-edit-trigger-${kind}`}
                  >
                    <ClipboardPen className="w-3 h-3 text-amber-600" />
                    Needs Review
                    {activeCategory && (
                      <>
                        <span className="text-amber-400 mx-0.5">·</span>
                        {activeCategory.color && (
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: activeCategory.color }}
                          />
                        )}
                        <span>{activeCategory.name}</span>
                      </>
                    )}
                    <Pencil className="w-2.5 h-2.5 ml-0.5 opacity-50" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              {question?.review_remarks && (
                <TooltipContent side="bottom" className="max-w-64 text-xs whitespace-pre-wrap leading-relaxed">
                  <p className="text-[10px] uppercase font-semibold tracking-wide mb-1 opacity-70">Remarks</p>
                  {question.review_remarks}
                </TooltipContent>
              )}
            </Tooltip>
          )}
          {popoverForm}
        </Popover>

        {/* Single-click resolve button — only when flagged */}
        {isFlagged && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleResolve}
                disabled={resolving}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 transition-colors disabled:opacity-40"
                data-testid={`review-chip-resolve-btn-${kind}`}
              >
                {resolving
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <CheckCircle2 className="w-3.5 h-3.5" />
                }
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Resolve review
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ReviewChip;
